import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { axiosBasic } from "../../../../api/axios";
import { config } from "../../../../api/endPoints";
import {
  buildPeriodQuery,
  getDashboardRoleType,
  mapAdminDashboardSummary,
  mapManagerDashboardSummary,
  mapUserDashboardSummary,
} from "./dashboardStatisticsUtils";

const MAPPERS = {
  admin: mapAdminDashboardSummary,
  manager: mapManagerDashboardSummary,
  user: mapUserDashboardSummary,
};

/**
 * @param {Date|null} periodFrom
 * @param {Date|null} periodTo
 * @param {{ usePeriod?: boolean }} options — when false, omits period query (for Today tab)
 */
export default function useDashboardSummary(periodFrom, periodTo, options = {}) {
  const { usePeriod = true, enabled = true } = options;
  const { t } = useTranslation();
  const { REST_API } = config;
  const roleType = getDashboardRoleType();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSummary = useCallback(async () => {
    if (!enabled) return;

    const endpoints = {
      admin: REST_API.Statistics.DashboardSummary,
      manager: REST_API.Statistics.ManagerDashboardSummary,
      user: REST_API.Statistics.UserDashboardSummary,
    };
    const endpoint = endpoints[roleType] ?? endpoints.user;
    const mapper = MAPPERS[roleType] ?? MAPPERS.user;

    try {
      setLoading(true);
      setError(null);
      const qs = usePeriod ? buildPeriodQuery(periodFrom, periodTo) : "";
      const res = await axiosBasic.get(endpoint + qs);
      if (res.data?.success && res.data.data) {
        setData(mapper(res.data.data));
      } else {
        setData(null);
        setError(res.data?.message ?? t("app.analytics.failedToLoadDashboard"));
      }
    } catch (err) {
      setData(null);
      setError(err.response?.data?.message ?? err?.message ?? t("app.analytics.failedToLoadDashboard"));
    } finally {
      setLoading(false);
    }
  }, [
    enabled,
    periodFrom,
    periodTo,
    usePeriod,
    roleType,
    REST_API.Statistics.DashboardSummary,
    REST_API.Statistics.ManagerDashboardSummary,
    REST_API.Statistics.UserDashboardSummary,
    t,
  ]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    fetchSummary();
  }, [fetchSummary, enabled]);

  return { data, loading, error, roleType, refetch: fetchSummary };
}

export { getCurrentMonthPeriod, getDashboardRoleType } from "./dashboardStatisticsUtils";
